import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheckDouble } from "@fortawesome/pro-solid-svg-icons";
import { faPen, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { MeasureData as TransformedToMeasureData } from "@treegram/viewer-core";

import useStoreyMeasuresContext from "../context/measures/use-storey-measures-context.ts";

import { Case, Switch } from "@/components/switch";

import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import Button from "@/components/buttons/button";
import Card from "@/components/card";

import {
    Measure3dViewerRef
} from "@/pages/takeoff/layouts/measures/layouts/storey-measures/components/measure-3d-viewer-container.tsx";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";
import usesCentimeters from "@/utils/measures/converters.ts";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import Tooltip from "@/components/tooltip";
import removeDoubleSpaces from "@/utils/text/remove-double-space.ts";
import {ModelingType} from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import {
    EditingProps
} from "@/pages/takeoff/layouts/measures/layouts/storey-measures/hooks/use-storey-measures-editing-tool.ts";

interface MeasureDetailsCardProps {
    setMeasureTypeIdToEdit: (measureType: string) => void;
    handleSelectMeasureType: (measureTypeId: string) => void;

    viewerRef: React.RefObject<Measure3dViewerRef | null>;
    getTransformedToMeasureDataFromGeometryId?: (geometryId: string) => TransformedToMeasureData | undefined;
    
    handleSave: () => Promise<boolean | void>;

    startDrawing: (measureType: MeasureType) => Promise<void>;
    startEditing:  (editingType: ModelingType, isSnappingEnabled: boolean, editingProps: EditingProps) => Promise<void>;
}

const MeasureDetailsCard: React.FC<MeasureDetailsCardProps> = ({
    setMeasureTypeIdToEdit,
    handleSelectMeasureType,
    viewerRef,
    getTransformedToMeasureDataFromGeometryId,
    handleSave,
    startDrawing,
    startEditing
}) => {

    const { t } = useTranslation();

    const {
        projectMeasureTypes,
        predefinedShapes,
        getDrawingTypeFromMeasureTypeId,
        getDrawingMeasureRepresentationFromMeasureTypeId,
    } = useProjectMeasureContext();
    const { selectedMeasures, measures, editMeasure, removeMeasure } = useStoreyMeasuresContext();

    const selectedMeasure = selectedMeasures?.measures[0];
    const isSingleViewerSelection = selectedMeasures?.origin === "viewer" && selectedMeasures.measures.length === 1;

    const clonedMeasure = useMemo(() => selectedMeasure?.clone(), [selectedMeasure]);

    const originalMeasureTypeId = useMemo(() => selectedMeasure?.measureTypeId, [selectedMeasure]);

    const types = useMemo(() => {
        const originalType = projectMeasureTypes.find(pmt => pmt.measureTypeId === originalMeasureTypeId);
        if (!originalType) return [];
        return projectMeasureTypes.filter(mt => mt.measureRepresentationId === originalType.measureRepresentationId)
            .sort((a, b) => removeDoubleSpaces(a.name).localeCompare(removeDoubleSpaces(b.name), 'fr', { sensitivity: 'base', numeric: true }))
    }, [projectMeasureTypes, originalMeasureTypeId]);

    const preSelectedItem = useMemo(() => projectMeasureTypes.find(mt => mt.measureTypeId === originalMeasureTypeId), [originalMeasureTypeId, projectMeasureTypes]);

    const [selectedItem, setSelectedItem] = useState(preSelectedItem);

    useEffect(() => {
        setSelectedItem(preSelectedItem);
    }, [preSelectedItem]);

    const currentShape = useMemo(() => {
        const shapeId = projectMeasureTypes.find(mt => mt.measureTypeId === clonedMeasure?.measureTypeId)?.measureShapeId;
        return predefinedShapes.find(ps => ps.measureShapeId === shapeId);
    }, [clonedMeasure?.measureTypeId, predefinedShapes, projectMeasureTypes]);

    const dataEntries = useMemo(() => {
        const records = clonedMeasure?.dataInformations;
        if (records == null) return undefined;

        if (typeof records === "object" && !Array.isArray(records) && !(records instanceof Map)) {
            return Object.entries(records as Record<string, unknown>);
        }

        return undefined;
    }, [clonedMeasure?.dataInformations]);

    const applyChanges = useCallback(async () => {
        if (selectedMeasure?.measureId == null || clonedMeasure == null) return;
        editMeasure(selectedMeasure.measureId, clonedMeasure);
        await handleSave();
    }, [selectedMeasure?.measureId, clonedMeasure, editMeasure, handleSave]);

    const dataFormatTypeEntries = useMemo(() => {
        if (dataEntries == null || selectedItem?.dataFormatType == null) return [];

        const keys = Object.keys(selectedItem.dataFormatType);

        return dataEntries.filter(([key]) => keys.includes(key));
    }, [dataEntries, selectedItem?.dataFormatType]);

    const dataFormatInstanceEntries = useMemo(() => {
        if (dataEntries == null || selectedItem?.dataFormatInstance == null) return [];

        const keys = Object.keys(selectedItem.dataFormatInstance);

        return dataEntries.filter(([key]) => keys.includes(key));
    }, [dataEntries, selectedItem?.dataFormatInstance]);

    const offsetValue = useMemo(() => dataEntries?.find(([key]) => key === "offset")?.[1], [dataEntries]);

    const changeMeasure = useCallback(async (selected: MeasureType | null | undefined) => {
        if (selected == null || clonedMeasure == null)
            return;

        clonedMeasure.setMeasureTypeId(selected.measureTypeId);
        setSelectedItem(selected);
        await applyChanges();
    }, [clonedMeasure, applyChanges])

    const formatValue = useCallback((key: string, value: unknown): string => {
        if (typeof value !== "number") {
            return String(value);
        }

        let valueToFormat = Number(value);
        const useCentimeters = usesCentimeters(key);
        if (useCentimeters)
            valueToFormat *= 100;

        return valueToFormat.toLocaleString("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: useCentimeters ? 0 : 2,
        });
    }, []);

    const handleStartDrawing = useCallback(async () => {
        if (!selectedMeasure) return;
        const measureType = projectMeasureTypes.find(mt => mt.measureTypeId === selectedMeasure.measureTypeId);
        if (!measureType) return;

        await startDrawing(measureType);
    }, [
        selectedMeasure,
        projectMeasureTypes,
        startDrawing,
    ]);

    const handleEditMeasure = useCallback(() => {
        if (!selectedMeasure || !getTransformedToMeasureDataFromGeometryId) return;
        const geometryId = selectedMeasure.geometryRepresentations[0]?.geometryId;
        if (!geometryId) return;

        const measureData = getTransformedToMeasureDataFromGeometryId(geometryId);
        if (!measureData) return;

        const editingType = getDrawingTypeFromMeasureTypeId(selectedMeasure.measureTypeId);
        startEditing(editingType, false, {
            measure: selectedMeasure,
            editingPoints: measureData.points,
            editingColor: measureData.color,
            editingAlpha: measureData.alpha,
            editingMeasureRepresentation: getDrawingMeasureRepresentationFromMeasureTypeId(selectedMeasure.measureTypeId),
            editingDataInformations: measureData.dataInformations,
        });
    }, [
        selectedMeasure,
        getTransformedToMeasureDataFromGeometryId,
        getDrawingTypeFromMeasureTypeId,
        startEditing,
        getDrawingMeasureRepresentationFromMeasureTypeId
    ]);

    if (selectedMeasures == null || selectedMeasures.measures.length === 0)
        return null;

    return (
        <>
            <Tooltip id='measure-details-card-tooltip' className='z-50' />
            <div className='flex flex-col gap-y-2 pointer-events-auto mt-auto'>
                {isSingleViewerSelection && selectedMeasure != null &&
                    <Card additionalContentClasses='py-2 px-4 text-sm flex flex-col gap-y-1'>
                        <div className='flex flex-row items-center w-full gap-x-px'>
                            <span className='flex-1'>Métré sélectionné</span>

                            <ButtonLockDependent data-tooltip-id='measure-details-card-tooltip'
                                data-tooltip-content='Editer le métré sélectionné'
                                variant='ghost' size='3xs' onClick={handleEditMeasure}>
                                <FontAwesomeIcon icon={faPen} className='text-gray-700' />
                            </ButtonLockDependent>
                            
                            <ButtonLockDependent data-tooltip-id='measure-details-card-tooltip' data-tooltip-content='Ajouter un métré de ce type'
                                                 variant='ghost' size='3xs'
                                                 onClick={handleStartDrawing}>
                                <FontAwesomeIcon icon={faPlus} className='text-green-500' />
                            </ButtonLockDependent>
                            
                            <ButtonLockDependent data-tooltip-id='measure-details-card-tooltip'
                                                 data-tooltip-content='Supprimer le métré sélectionné'
                                                 variant='ghost' size='3xs' onClick={() => {
                                viewerRef.current?.removeMeasure(selectedMeasure.measureId);
                                removeMeasure(selectedMeasure.measureId);
                            }}>
                                <FontAwesomeIcon icon={faTrash} className='text-red-500' />
                            </ButtonLockDependent>

                        </div>

                        <div className='flex flex-col gap-y-1.5 pt-1'>
                            <div className={`flex flex-row items-center w-full gap-x-2 text-xs pb-2 ${dataFormatInstanceEntries.length !== 0 ? 'border-b' : ''}`}>
                                <div>Type :</div>
                                <Select getOptionLabel={option => option.name}
                                    getOptionValue={option => option.measureTypeId}
                                    options={types} value={selectedItem} isSearchable={false}
                                    menuPlacement="auto"
                                    classNames={{
                                        container: () => "flex-1 h-6",
                                        control: () => "!min-h-0 !rounded-md !border-gray-200 h-6",
                                        valueContainer: () => "!py-0 !px-2",
                                        singleValue: () => "text-xs !m-0",
                                        placeholder: () => "text-xs !m-0",
                                        input: () => "!m-0 !p-0",
                                        indicatorSeparator: () => "!h-3 !my-auto",
                                        dropdownIndicator: () => "!p-1 [&_svg]:!size-3",
                                        indicatorsContainer: () => "h-6",
                                        menu: () => "text-xs",
                                        menuList: () => "text-xs",
                                    }}
                                    onChange={selected => changeMeasure(selected)}>
                                </Select>
                            </div>

                            {dataFormatInstanceEntries && dataFormatInstanceEntries.length > 0 &&
                                <div className='grid grid-cols-[auto_1fr] gap-y-1.5 gap-x-4 text-xs w-full items-center'>
                                    {dataFormatInstanceEntries.map(([key, value]) => {
                                        if (value == null) return null;
                                        const unit = t(`${key}_unit`, { defaultValue: "" });
                                        return (
                                            <React.Fragment key={key}>
                                                <div className='my-auto'>
                                                    <Trans>{key}</Trans> {unit !== "" && <span> ({unit})</span>} :
                                                </div>
                                                <Switch value={key}>
                                                    <Case value='color'>
                                                        <div className="w-5 h-5 rounded border"
                                                            style={{ backgroundColor: value as string }}></div>
                                                    </Case>
                                                    <Case value='alpha'>
                                                        <div>{value as number * 100} %</div>
                                                    </Case>
                                                    <Case default>
                                                        <div>{formatValue(key, value)}</div>
                                                    </Case>
                                                </Switch>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            }
                        </div>
                    </Card>
                }

                <Card additionalContentClasses='flex flex-col text-sm'>
                    <div className='flex flex-col py-2 px-4 gap-y-2'>
                        <div className='flex flex-row items-center w-full gap-x-px'>
                            <div className='flex-1'>Type</div>

                            <Button size='3xs' variant='ghost' data-tooltip-id='measure-details-card-tooltip' data-tooltip-content='Sélectionner tous les objets de ce type de métré'
                                onClick={() => {
                                    if (selectedItem) handleSelectMeasureType(selectedItem.measureTypeId)
                                }}>
                                <FontAwesomeIcon icon={faCheckDouble} />
                            </Button>

                            <ButtonLockDependent variant='ghost' size='3xs' data-tooltip-id='measure-details-card-tooltip' data-tooltip-content='Editer ce type de métré' onClick={() => {
                                if (selectedItem) {
                                    setMeasureTypeIdToEdit(selectedItem.measureTypeId);
                                }
                            }}>
                                <FontAwesomeIcon icon={faPen} />
                            </ButtonLockDependent>
                        </div>

                        <div className='flex flex-col gap-y-1'>
                            <div className='flex flex-row pb-1 items-center gap-x-1 border-b'>

                                <Switch
                                    value={currentShape?.representations.find(r => r.measureRepresentationId === preSelectedItem?.measureRepresentationId)?.name}>
                                    <Case value='LineVolume'>
                                        <span>Mur</span>
                                    </Case>
                                    <Case value='LineVolumeOffset'>
                                        {offsetValue != null &&
                                            <>
                                                {offsetValue === 0 ? (
                                                    <span>Porte</span>
                                                ) : (
                                                    <span>Fenêtre</span>
                                                )}
                                            </>
                                        }
                                    </Case>
                                    <Case value='LineVolumeDoor'>
                                        <span>Porte</span>
                                    </Case>
                                    <Case value='Extrusion'>
                                        <span>Dalle</span>
                                    </Case>
                                    <Case value='ExtrusionRoom'>
                                        <span>Pièce</span>
                                    </Case>
                                    <Case default>
                                        <span>Non déterminé</span>
                                    </Case>
                                </Switch>

                                <span className='ml-auto text-gray-600 text-xs'> ( x {measures.filter(m => m.measureTypeId === selectedItem?.measureTypeId).length} )</span>
                            </div>

                            <div className='flex flex-row gap-x-1 text-xs'>
                                <div className='grid grid-cols-[auto_1fr] gap-y-1.5 gap-x-4 items-center flex-1'>
                                    {dataFormatTypeEntries && dataFormatTypeEntries.map(([key, value]) => {
                                        if (value == null) return null;
                                        const unit = t(`${key}_unit`, { defaultValue: "" });
                                        return (
                                            <React.Fragment key={key}>
                                                <div className='my-auto'><Trans>{key}</Trans> {unit !== "" &&
                                                    <span> ({unit})</span>}:
                                                </div>
                                                <Switch value={key}>
                                                    <Case value='color'>
                                                        <div className="w-5 h-5 rounded"
                                                            style={{ backgroundColor: value as string }}></div>
                                                    </Case>
                                                    <Case value='alpha'>
                                                        <div>{value as number * 100} %</div>
                                                    </Case>
                                                    <Case default>
                                                        <div>{formatValue(key, value)}</div>
                                                    </Case>
                                                </Switch>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

        </>
    );
}

export default MeasureDetailsCard;